<template>
  <Section>
    <EditPasswordModal
      :accountId="userId"
      :onCloseModal="handlePasswordModal"
      :onChangePassword="changePassword"
      v-if="passwordModal"/>
    <ConfirmModal
      v-if="changeUserStatusModal"
      :onClose="toggleStatusModal"
      :onConfirm="changeUserStatus">
      <template v-slot:header-title>
        <h4 v-if="userSuspended">ยืนยันเปิดใช้งานบัญชี</h4>
        <h4 v-if="!userSuspended">ยืนยันการระงับบัญชี</h4>
      </template>
      <div v-if="userSuspended">
        การเปิดใช้งานบัญชี
        {{ user.name }}
        จะส่งผลให้ผู้ใช้งานบัญชีนี้ สามารถกลับมาใช้งานระบบได้
      </div>
      <div v-if="!userSuspended">
        การเปิดใช้งานบัญชี
        {{ user.name }}
        จะส่งผลให้ผู้ใช้งานบัญชีนี้ ไม่สามารถเข้าใช้งานระบบได้
      </div>
      <div class="pt-3">
        <small class="text-danger" v-if="isServerError()">
          {{getErrorMessageFromErrorStatus(errorStatus)}}
        </small>
      </div>
    </ConfirmModal>
    <template v-slot:section-header>
      <h5 class="title">ข้อมูลผู้ใช้</h5>
    </template>
    <template v-slot:section-body>
      <form @submit.prevent>
        <div class="row">
          <div class="col-12 order-last col-md-7 order-md-first">
            <FormGroup>
              <h5 class="name">ชื่อ</h5>
              <input
                type="text"
                :value="user.name"
                placeholder="ชื่อผู้ใช้"
                class="form-control"
                :class="{ 'is-invalid': saveUserStateError.name }"
                @input="$emit('update:user', { ...user, name: $event.target.value })"
                @blur="validateName"
                @keyup="validateName"/>
              <div class="invalid-feedback">{{ saveUserStateError.name }}</div>
            </FormGroup>
            <FormGroup>
              <h5 class="name">อีเมล</h5>
              <input type="email"
                :value="user.email"
                placeholder="อีเมลล์"
                class="form-control"
                disabled>
            </FormGroup>
            <FormGroup v-if="isAdmin">
              <h5 class="name">บทบาท</h5>
              <select @change="$emit('update:user', { ...user, roleId: $event.target.value })"
                :value="user.roleId" class="form-control">
                <option value="editor">ผู้จัดการคอนเทนท์</option>
                <option value="admin">ผู้ดูแลระบบ</option>
              </select>
            </FormGroup>
            <FormGroup>
              <div class="actions">
                <div
                  class="btn btn-link pl-0"
                  @click="handlePasswordModal">
                  แก้ไขรหัสผ่าน
                </div>
                <small
                  class="text-success font-weight-bold ml-2"
                  v-if="passwordEdited">
                  <i class="fa fa-check"></i>
                  บันทึกแล้ว
                </small>
              </div>
            </FormGroup>
            <FormGroup v-if="!editProfile">
              <button
                @click="toggleStatusModal"
                v-if="userSuspended"
                type="button"
                class="btn btn-outline-success">
                เปิดใช้งานใหม่
              </button>
              <button
                @click="toggleStatusModal"
                v-if="!userSuspended"
                type="button"
                class="btn btn-outline-danger">
                ระงับบัญชี
              </button>
            </FormGroup>
          </div>
          <div class="col-12 order-first col-md-5 col-lg-3 order-md-last">
            <FormGroup>
              <UserImageContainer :image.sync="user.displayImage" />
            </FormGroup>
          </div>
        </div>
      </form>
    </template>
  </Section>
</template>

<script>
/* eslint-disable no-underscore-dangle */

import _ from 'lodash';
import {
  suspendUser,
  activateUser,
} from '@/user/api';
import { mapGetters } from 'vuex';
import Section from '@/components/Section.vue';
import FormGroup from '@/components/FormGroup.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import UserImageContainer from './UserImageContainer.vue';
import EditPasswordModal from './EditPasswordModal.vue';

export default {
  props: {
    user: {
      type: Object,
      default: () => {},
    },
    editProfile: {
      type: Boolean,
      default: () => false,
    },
    saveUserStateError: {
      type: Object,
      default: () => {},
    },
  },
  components: {
    Section,
    UserImageContainer,
    FormGroup,
    ConfirmModal,
    EditPasswordModal,
  },
  data() {
    return {
      errorStatus: null,
      passwordEdited: false,
      passwordModal: false,
      changeUserStatusModal: false,
    };
  },
  computed: {
    ...mapGetters(['currentUser']),
    isAdmin() {
      return this.currentUser.roleId === 'admin';
    },
    userId() {
      return _.get(this.user, 'id', this.user._id);
    },
    accountId() {
      return this.$route.params.accountId;
    },
    userSuspended() {
      if (this.user.status === 'suspended') return true;
      return false;
    },
  },
  methods: {
    async suspendUser() {
      try {
        await suspendUser(this.accountId);
        this.errorStatus = null;
        this.$router.push('/accounts');
      } catch (error) {
        this.errorStatus = error.response.status;
      }
    },
    async activateUser() {
      try {
        await activateUser(this.accountId);
        this.errorStatus = null;
        this.$router.push('/accounts');
      } catch (error) {
        this.errorStatus = error.response.status;
        this.saveStatusState.error = error.response.data;
      }
    },
    changeUserStatus() {
      if (this.userSuspended) {
        this.activateUser();
        return;
      }
      this.suspendUser();
    },
    isServerError() {
      return this.errorStatus;
    },
    getErrorMessageFromErrorStatus(errorStatus) {
      if (errorStatus === 500 || errorStatus === 404) {
        return `พบข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองอีกครั้งหรือติดต่อผู้ดูแลระบบ code : ${errorStatus}`;
      }
      if (errorStatus === 422 || errorStatus === 409) {
        return 'กรอกข้อมูลไม่ถูกต้อง กรุณาตรวจสอบข้อมูลอีกครั้ง';
      }
    },
    validateName(event) {
      if (!event.target.value) {
        this.$emit('update:saveUserStateError', { ...this.saveUserStateError, name: 'กรุณากรอกชื่อที่ต้องการใช้แสดงผล' });
      } else {
        this.$emit('update:saveUserStateError', { ...this.saveUserStateError, name: null });
      }
    },
    handlePasswordModal() {
      this.passwordModal = !this.passwordModal;
    },
    changePassword() {
      this.passwordEdited = true;
    },
    toggleStatusModal() {
      this.changeUserStatusModal = !this.changeUserStatusModal;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/components/form.scss';
@import './src/assets/scss/themes/components/section-container.scss';
</style>
